import { useState, useEffect, useCallback, memo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CiCircleCheck,
  CiMoneyBill,
  CiFileOn,
  CiFilter,
  CiCircleRemove,
  CiUser,
} from "react-icons/ci";
import ButtonLightTextIcon from "../../../components/ui/ButtonLightTextIcon";
import ButtonTextIcon from "../../../components/ui/ButtonTextIcon";
import {
  API_URL,
  APP_CURRENCY,
  DEFAULT_PER_PAGE,
} from "../../../utilities/constants";
import FullPageLoader from "../../../components/ui/FullPageLoader";
import ResultsPerPage from "../../_common/ResultsPerPage";
import Pagination from "../../../components/common/Pagination";
import RSModal from "../../../components/ui/RSModal";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { formatAmountValue, formatDateTime } from "../../../utilities/helpers";
import PageTitle from "../../../components/common/PageTitle";
import { useUI } from "../../../context/ui";
import { buildQueryParams } from "../../../utilities/helpers";
import { RiArrowGoBackFill } from "react-icons/ri";
const formatFiltersAsReadableString = (filters) =>
  Object.entries(filters)
    .filter(
      ([_, value]) => value !== "" && value !== undefined && value !== null
    )
    .map(([key, value]) => {
      let readableKey;
      switch (key) {
        case "transactionType":
          readableKey = "Type";
          break;
        case "transactionCategory":
          readableKey = "Category";
          break;
        case "fromDate":
          readableKey = "From Date";
          break;
        case "toDate":
          readableKey = "To Date";
          break;
        default:
          readableKey =
            key.charAt(0).toUpperCase() +
            key.slice(1).replace(/([A-Z])/g, " $1");
      }
      return readableKey;
    })
    .join(", ");

// FieldTitle component
const FieldTitle = memo(({ title, required }) => (
  <p
    className="title f-w-700 color-primary"
    style={{ marginBottom: "5px", fontSize: "14px" }}
  >
    {title}
    {required && <span style={{ color: "red", marginLeft: "4px" }}>*</span>}
  </p>
));

// TransactionCard component for table rows
const TransactionCard = memo(({ trx, index }) => {
  const isCredit = trx.transactionType.includes("CREDIT");
  const formattedDateTime = formatDateTime(trx.createdAt);

  const getCategoryInfo = () => {
    switch (trx.transactionCategory) {
      case "BONUS":
        return {
          description: `Bonus Reward Earned - ${trx.transactionRefNumber}`,
        };
      case "CLAIM":
        return { description: `Reward Claimed - ${trx.transactionRefNumber}` };
      case "CONVERT":
        return {
          description: `Reward Converted - ${trx.transactionRefNumber}`,
        };
      default:
        return {
          description: `Base Reward Earned - ${trx.transactionRefNumber}`,
        };
    }
  };

  const categoryInfo = getCategoryInfo();

  return (
    <tr style={{ backgroundColor: isCredit ? "#EAF8F4" : "#FFEBEB" }}>
      <td>{index + 1}</td>
      <td>{trx.transactionRefNumber || "-"}</td>
      <td>{trx.transactionCategory || "-"}</td>
      <td className="f-w-700">{isCredit ? "Credit" : "Debit"}</td>
      <td>
        {APP_CURRENCY} {formatAmountValue(trx.amount)}/-
      </td>
      <td>{formatAmountValue(trx.postBalance)}</td>
      <td>{formattedDateTime}</td>
      <td>{categoryInfo.description}</td>
    </tr>
  );
});

// FilterContent component for modal
const FilterContent = ({ setShow, initialValues, pageSize }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const validationSchema = Yup.object({
    fromDate: Yup.string()
      .nullable()
      .test(
        "is-not-future-date",
        "From date cannot be in the future",
        function (value) {
          if (!value) return true;
          const selectedDate = new Date(value);
          const today = new Date();
          return selectedDate <= today;
        }
      )
      .test(
        "is-before-toDate",
        "From date cannot be after To date",
        function (value) {
          const { toDate } = this.parent;
          if (!value || !toDate) return true;
          const fromDate = new Date(value);
          const endDate = new Date(toDate);
          return fromDate <= endDate;
        }
      ),
    toDate: Yup.string()
      .nullable()
      .test(
        "is-not-future-date",
        "To date cannot be in the future",
        function (value) {
          if (!value) return true;
          const selectedDate = new Date(value);
          const today = new Date();
          return selectedDate <= today;
        }
      )
      .test(
        "is-after-fromDate",
        "To date cannot be before From date",
        function (value) {
          const { fromDate } = this.parent;
          if (!value || !fromDate) return true;
          const endDate = new Date(value);
          const startDate = new Date(fromDate);
          return endDate >= startDate;
        }
      ),
    transactionType: Yup.string(),
    transactionCategory: Yup.string(),
  });

  return (
    <div className="pos-rel" style={{ width: "480px", padding: "40px" }}>
      <CiCircleRemove
        className="cursor-pointer transition hover-color-primary"
        onClick={() => setShow(false)}
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          fontSize: "32px",
        }}
      />
      <h4 className="color-primary">Filter Transactions</h4>
      <br />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={(values) => {
          const queryParams = buildQueryParams({
            pageNo: 0,
            pageSize,
            transactionType: values.transactionType,
            transactionCategory: values.transactionCategory,
            fromDate: values.fromDate,
            toDate: values.toDate,
          });
          navigate(`${location.pathname}?${queryParams}`);
          setShow(false);
        }}
      >
        {({ errors, touched, resetForm, setFieldValue }) => (
          <Form>
            <div className="flex center-center" style={{ gap: "10px" }}>
              <div style={{ width: "100%" }}>
                <FieldTitle title="From Date" />
                <Field
                  name="fromDate"
                  type="date"
                  className={`col-1-1 ${
                    errors.fromDate && touched.fromDate ? "error" : ""
                  }`}
                />
                <ErrorMessage
                  name="fromDate"
                  component="p"
                  className="error-messages"
                />
              </div>
              <div style={{ width: "100%" }}>
                <FieldTitle title="To Date" />
                <Field
                  name="toDate"
                  type="date"
                  className={`col-1-1 ${
                    errors.toDate && touched.toDate ? "error" : ""
                  }`}
                />
                <ErrorMessage
                  name="toDate"
                  component="p"
                  className="error-messages"
                />
              </div>
            </div>
            <br />
            <div className="flex center-center" style={{ gap: "10px" }}>
              <div style={{ width: "100%" }}>
                <FieldTitle title="Transaction Type" />
                <Field
                  name="transactionType"
                  as="select"
                  className={`col-1-1 ${
                    errors.transactionType && touched.transactionType
                      ? "error"
                      : ""
                  }`}
                >
                  <option value="">All Types</option>
                  <option value="LOYALTY_CREDIT">Credit</option>
                  <option value="LOYALTY_DEBIT">Debit</option>
                </Field>
              </div>
              <div style={{ width: "100%" }}>
                <FieldTitle title="Transaction Category" />
                <Field
                  name="transactionCategory"
                  as="select"
                  className={`col-1-1 ${
                    errors.transactionCategory && touched.transactionCategory
                      ? "error"
                      : ""
                  }`}
                >
                  <option value="">All Categories</option>
                  <option value="BONUS">Bonus</option>
                  <option value="CLAIM">Claim</option>
                  <option value="CONVERT">Convert</option>
                  <option value="BASE">Base</option>
                </Field>
              </div>
            </div>
            <br />
            <div className="flex center-left" style={{ gap: "10px" }}>
              <ButtonLightTextIcon
                type="button"
                title="Reset"
                onClick={() => {
                  resetForm();
                  setShow(false);
                  navigate(
                    `${location.pathname}?pageNo=0&pageSize=${pageSize}`
                  );
                }}
                icon={<CiCircleRemove size={20} />}
              />
              <ButtonTextIcon
                type="submit"
                title="Search"
                icon={<CiCircleCheck size={20} />}
                className="responsive-button"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const CustomerDetails = () => {
  const { id } = useParams();
  const [loadingWalletData, setLoadingWalletData] = useState(true);
  const [loadingTrxData, setLoadingTrxData] = useState(false);
  const [data, setData] = useState(null);
  const [walletTrx, setWalletTrx] = useState({
    transactions: [],
    totalBaseAmount: 0,
    totalBonusAmount: 0,
    totalClaimAmount: 0,
    totalConvertAmount: 0,
    totalPages: 0,
    totalRecords: 0,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageNo = parseInt(searchParams.get("pageNo") || 0);
  const pageSize = parseInt(searchParams.get("pageSize") || DEFAULT_PER_PAGE);
  const transactionType = searchParams.get("transactionType") || "";
  const transactionCategory = searchParams.get("transactionCategory") || "";
  const fromDate = searchParams.get("fromDate") || "";
  const toDate = searchParams.get("toDate") || "";
  const { setSegmentTitle } = useUI();

  // Fetch wallet data
  const fetchWalletData = useCallback(async () => {
    setLoadingWalletData(true);
    try {
      const resp = await fetch(
        `${API_URL}/gasolina/api/1.0.0/octorewards/customers/wallets?externalCustomerId=${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-JWT-Assertion": localStorage.getItem("token"),
          },
        }
      );
      if (resp.ok) {
        const response = await resp.json();
        setData(response.data);
      }
    } catch (e) {
      console.error("Error fetching customer details", e);
    } finally {
      setLoadingWalletData(false);
    }
  }, [id]);

  // Fetch transaction data
  const fetchTransactionData = useCallback(
    async (wallet, page) => {
      setLoadingTrxData(true);
      try {
        const queryParams = new URLSearchParams({
          pageNo: page,
          pageSize,
          sortOrder: "DESC",
          ...(transactionType && { transactionType }),
          ...(transactionCategory && { transactionCategory }),
          ...(fromDate && { startDate: `${fromDate}T00:00:00` }),
          ...(toDate && { endDate: `${toDate}T23:59:59` }),
        });

        const resp = await fetch(
          `${API_URL}/gasolina/api/1.0.0/octorewards/wallets/${wallet}/transactions?${queryParams.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "X-JWT-Assertion": localStorage.getItem("token"),
            },
          }
        );
        if (resp.ok) {
          const response = await resp.json();
          const newTransactions = response.data?.transactions || [];
          setWalletTrx({
            transactions: newTransactions,
            totalBaseAmount: response.data.totalBaseAmount || 0,
            totalBonusAmount: response.data.totalBonusAmount || 0,
            totalClaimAmount: response.data.totalClaimAmount || 0,
            totalConvertAmount: response.data.totalConvertAmount || 0,
            totalPages: response.data.totalPages || 0,
            totalRecords: response.data.totalRecords || 0,
          });
        }
      } catch (e) {
        console.error("Error fetching customer wallet transaction", e);
      } finally {
        setLoadingTrxData(false);
      }
    },
    [pageSize, transactionType, transactionCategory, fromDate, toDate]
  );

  // Initial wallet data fetch
  useEffect(() => {
    setSegmentTitle("Customer Details");
    fetchWalletData();
  }, [fetchWalletData, setSegmentTitle]);

  // Fetch transactions when wallet data is available or page/filters change
  useEffect(() => {
    const firstWallet = data?.wallets?.[0];
    if (firstWallet?.accountNumber && !loadingWalletData) {
      fetchTransactionData(firstWallet.accountNumber, pageNo);
    }
  }, [
    data,
    pageNo,
    fetchTransactionData,
    loadingWalletData,
    transactionType,
    transactionCategory,
    fromDate,
    toDate,
  ]);

  return (
    <section className="pos-rel">
      <FullPageLoader isActive={loadingWalletData || loadingTrxData} />
      <PageTitle title="Customer Details" />

      {data && !loadingWalletData && (
        <div className="dashboard">
          {/* Customer and Wallet Info Cards */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              padding: "0 20px",
              gap: "20px",
            }}
          >
            <div
              style={{
                flex: "1",
                minWidth: "300px",
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "20px",
              }}
            >
              <h3
                className="color-primary f-w-700"
                style={{ fontSize: "18px", marginBottom: "20px" }}
              >
                Customer Information
              </h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
                <div className="flex center-left" style={{ gap: "10px" }}>
                  <CiUser
                    className="color-secondary"
                    style={{ height: "40px", width: "auto" }}
                  />
                  <div>
                    <p className="color-faded font-size-12 f-w-400">
                      Customer Name
                    </p>
                    <h6 className="color-primary f-w-700">
                      {data?.fullName || "-"}
                    </h6>
                  </div>
                </div>
                <div className="flex center-left" style={{ gap: "10px" }}>
                  <CiFileOn
                    className="color-secondary"
                    style={{ height: "40px", width: "auto" }}
                  />
                  <div>
                    <p className="color-faded font-size-12 f-w-400">
                      Wallet Number
                    </p>
                    <h6 className="color-primary f-w-700">
                      {data?.wallets[0]?.accountNumber || "-"}
                    </h6>
                  </div>
                </div>
                <div className="flex center-left" style={{ gap: "10px" }}>
                  <CiMoneyBill
                    className="color-secondary"
                    style={{ height: "40px", width: "auto" }}
                  />
                  <div>
                    <p className="color-faded font-size-12 f-w-400">
                      Wallet Balance
                    </p>
                    <h6 className="color-primary f-w-700">
                      {APP_CURRENCY}{" "}
                      {formatAmountValue(data?.wallets[0]?.balance || 0)}/-
                    </h6>
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                flex: "1",
                minWidth: "300px",
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "20px",
              }}
            >
              <h3
                className="color-primary f-w-700"
                style={{ fontSize: "18px", marginBottom: "20px" }}
              >
                Transaction Summary
              </h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
                <div className="flex center-left" style={{ gap: "10px" }}>
                  <CiMoneyBill
                    className="color-secondary"
                    style={{ height: "40px", width: "auto" }}
                  />
                  <div>
                    <p className="color-faded font-size-12 f-w-400">
                      Total Base Amount
                    </p>
                    <h6 className="color-primary f-w-700">
                      {APP_CURRENCY}{" "}
                      {formatAmountValue(walletTrx.totalBaseAmount)}/-
                    </h6>
                  </div>
                </div>
                <div className="flex center-left" style={{ gap: "10px" }}>
                  <CiMoneyBill
                    className="color-secondary"
                    style={{ height: "40px", width: "auto" }}
                  />
                  <div>
                    <p className="color-faded font-size-12 f-w-400">
                      Total Bonus Amount
                    </p>
                    <h6 className="color-primary f-w-700">
                      {APP_CURRENCY}{" "}
                      {formatAmountValue(walletTrx.totalBonusAmount)}/-
                    </h6>
                  </div>
                </div>
                <div className="flex center-left" style={{ gap: "10px" }}>
                  <CiMoneyBill
                    className="color-secondary"
                    style={{ height: "40px", width: "auto" }}
                  />
                  <div>
                    <p className="color-faded font-size-12 f-w-400">
                      Total Claim Amount
                    </p>
                    <h6 className="color-primary f-w-700">
                      {APP_CURRENCY}{" "}
                      {formatAmountValue(walletTrx.totalClaimAmount)}/-
                    </h6>
                  </div>
                </div>
                <div className="flex center-left" style={{ gap: "10px" }}>
                  <CiMoneyBill
                    className="color-secondary"
                    style={{ height: "40px", width: "auto" }}
                  />
                  <div>
                    <p className="color-faded font-size-12 f-w-400">
                      Total Convert Amount
                    </p>
                    <h6 className="color-primary f-w-700">
                      {APP_CURRENCY}{" "}
                      {formatAmountValue(walletTrx.totalConvertAmount)}/-
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Results Per Page */}
          <div className="flex center-left responsive-container">
            <div className="flex center-left" style={{ gap: "10px" }}>
              <ButtonTextIcon
                type="button"
                title="Go Back"
                onClick={() => navigate("/rewards/customers")}
                icon={<RiArrowGoBackFill />}
                className="responsive-button"
              />
              <ResultsPerPage
                targetURL={`${location.pathname}?${buildQueryParams({
                  transactionType,
                  transactionCategory,
                  fromDate,
                  toDate,
                })}&pageNo=0&pageSize=`}
              />
            </div>
            {/* ADD Go Back Button */}

            <div className="responsive-button">
              <RSModal
                selector={
                  <ButtonTextIcon
                    type="button"
                    title="Filter"
                    icon={<CiFilter />}
                  />
                }
                content={
                  <FilterContent
                    setShow={(val) => val}
                    initialValues={{
                      transactionType,
                      transactionCategory,
                      fromDate,
                      toDate,
                    }}
                    pageSize={pageSize}
                  />
                }
              />
            </div>
          </div>
          {/* Transactions Table */}
          <div className="results">
            <div className="rs-table-holder">
              <table className="rs-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Transaction Ref</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Balance</th>
                    <th>Date</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {!loadingTrxData && walletTrx.transactions.length > 0
                    ? walletTrx.transactions.map((trx, i) => (
                        <TransactionCard
                          key={trx.transactionRefNumber}
                          trx={trx}
                          index={pageNo * pageSize + i}
                        />
                      ))
                    : !loadingTrxData && (
                        <tr>
                          <td
                            colSpan={8}
                            style={{ textAlign: "center", padding: "10px" }}
                          >
                            <p>No transactions found</p>
                          </td>
                        </tr>
                      )}
                </tbody>
              </table>
              {loadingTrxData && (
                <div className="flex center-center" style={{ padding: "20px" }}>
                  <p>Loading transactions...</p>
                </div>
              )}
            </div>
            {walletTrx.totalRecords > 0 && (
              <Pagination
                totalRecords={walletTrx.totalRecords}
                pageNo={pageNo}
                pageSize={pageSize}
                baseUrl={`${location.pathname}?${buildQueryParams({
                  transactionType,
                  transactionCategory,
                  fromDate,
                  toDate,
                })}&`}
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default memo(CustomerDetails);
